import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import SectionHead from './SectionHead'
import Reveal from './Reveal'
import { usePlaces, useDistance } from '../i18n/hooks'
import { useI18n } from '../i18n/LanguageContext'

const escapeHtml = (text) =>
  text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])

function MapSection() {
  const { t } = useI18n()
  const { places, categories } = usePlaces()
  const distance = useDistance()
  const mapPlaces = places.filter((place) => place.lat != null)
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef({})
  const [activeId, setActiveId] = useState(null)

  const popupHtml = (place) => `
    <div class="map-popup">
      <span class="map-popup-tag">${escapeHtml(categories[place.category])}</span>
      <strong>${escapeHtml(place.shortName ?? place.name)}</strong>
      <p>${escapeHtml(place.summary)}</p>
      ${place.id === 'nanovitsa' ? '' : `<span class="map-popup-distance">${escapeHtml(distance(place))} ${escapeHtml(t.map.distanceSuffix)}</span>`}
    </div>`

  useEffect(() => {
    const bounds = L.latLngBounds(mapPlaces.map((place) => [place.lat, place.lon]))
    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      dragging: !L.Browser.mobile,
      zoomSnap: 0.5,
    })
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map)
    map.fitBounds(bounds, { padding: [48, 48] })

    mapPlaces.forEach((place) => {
      const icon = L.divIcon({
        className: 'map-pin-wrap',
        html: `<span class="map-pin cat-${place.category}${place.id === 'nanovitsa' ? ' is-home' : ''}"></span>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -14],
      })
      const marker = L.marker([place.lat, place.lon], { icon, title: place.shortName ?? place.name })
        .addTo(map)
        .bindPopup(popupHtml(place), { maxWidth: 260 })
      marker.on('click', () => setActiveId(place.id))
      markersRef.current[place.id] = marker
    })

    map.on('focus', () => map.scrollWheelZoom.enable())
    map.on('blur', () => map.scrollWheelZoom.disable())
    map.on('popupclose', () => setActiveId(null))

    mapRef.current = map
    const markers = markersRef.current
    return () => {
      map.remove()
      mapRef.current = null
      Object.keys(markers).forEach((key) => delete markers[key])
    }
    // Rebuild the whole map when the language changes, since popup text is baked into Leaflet's HTML.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  const focusPlace = (place) => {
    const map = mapRef.current
    if (!map) return
    map.flyTo([place.lat, place.lon], Math.max(map.getZoom(), 12), { duration: 0.8 })
    markersRef.current[place.id]?.openPopup()
    setActiveId(place.id)
  }

  const resetView = () => {
    const bounds = L.latLngBounds(mapPlaces.map((place) => [place.lat, place.lon]))
    mapRef.current?.closePopup()
    mapRef.current?.flyToBounds(bounds, { padding: [48, 48], duration: 0.8 })
    setActiveId(null)
  }

  return (
    <section className="section" id="map" aria-labelledby="map-title">
      <div className="container">
        <SectionHead id="map-title" kicker={t.map.kicker} title={t.map.title} lead={t.map.lead} />
        <Reveal className="map-layout">
          <div className="map-panel">
            <div ref={containerRef} className="map-canvas" role="region" aria-label={t.map.ariaMap} />
          </div>
          <div className="map-list">
            <ul>
              {mapPlaces.map((place) => (
                <li key={place.id}>
                  <button
                    type="button"
                    className={`map-item${activeId === place.id ? ' is-active' : ''}`}
                    onClick={() => focusPlace(place)}
                  >
                    <span className={`map-dot cat-${place.category}`} aria-hidden="true" />
                    <span className="map-item-name">{place.shortName ?? place.name}</span>
                    <span className="map-item-distance">
                      {place.id === 'nanovitsa' ? t.map.youAreHere : distance(place)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" className="button button-small" onClick={resetView}>{t.map.showAll}</button>
            <p className="map-hint">{t.map.hint}</p>
          </div>
        </Reveal>
      </div>
    </section>
  )
}

export default MapSection
